import React, { useMemo, useState } from 'react';
import {
  CalendarDays, CircleCheck, Hash, Tag, TriangleAlert,
} from 'lucide-react';
import Block from './Block';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Select } from '../../components/ui/select';

/**
 * Renders a `data_analysis` block from the analyze_data tool. Backend payload:
 *   { total_rows, n_features, features: [...], warnings: [...], correlations: [...] }
 *   features[i]: { name, type: 'numerical'|'categorical'|'date',
 *                  stats: {mean,median,min,max,std,count,null_count,...} | {most_common,unique_count,...},
 *                  histogram: {counts, bin_edges} | {categories, counts} }
 *   correlations[i]: { col1, col2, value }   // already sorted by |value|, deduped
 *   target_column: str                      // '' when no target agreed yet;
 *                                          // absent on blocks predating it,
 *                                          // hence the targetColumn prop
 */

// -----------------------------------------------------------------------------
// Mini bar chart (used for both numeric histograms and categorical bars).
// -----------------------------------------------------------------------------

const MiniHistogram = ({ values = [], labels = [], height = 64 }) => {
  const max = Math.max(...values, 0.0001);
  return (
    <div className="q-hist" style={{ height }}>
      {values.map((v, i) => (
        <div
          key={i}
          className="q-hist__bar"
          title={labels[i] != null ? `${labels[i]}: ${v.toLocaleString()}` : `${v}`}
          style={{ height: `${Math.max(3, (v / max) * 100)}%` }}
        />
      ))}
    </div>
  );
};

// -----------------------------------------------------------------------------
// Per-feature card.
// -----------------------------------------------------------------------------

const fmtNum = (v) => {
  if (v == null || Number.isNaN(Number(v))) return '–';
  const n = Number(v);
  if (Number.isInteger(n) || Math.abs(n) >= 1000) return n.toLocaleString(undefined, { maximumFractionDigits: 0 });
  if (Math.abs(n) >= 1) return n.toFixed(2);
  return n.toFixed(3);
};

const TYPE_META = {
  numerical: { icon: Hash, label: 'Numeric' },
  categorical: { icon: Tag, label: 'Categorical' },
  date: { icon: CalendarDays, label: 'Date' },
};

const FeatureCard = ({ feature, warning }) => {
  const type = feature?.type || 'unknown';
  const stats = feature?.stats || {};
  const histogram = feature?.histogram || {};
  const typeMeta = TYPE_META[type] || { icon: Hash, label: type };
  const TypeIcon = typeMeta.icon;

  let histValues = [];
  let histLabels = [];
  if (type === 'numerical' && Array.isArray(histogram.counts) && Array.isArray(histogram.bin_edges)) {
    histValues = histogram.counts;
    histLabels = histogram.bin_edges.slice(0, -1).map((e, i) =>
      `${Number(e).toFixed(2)}–${Number(histogram.bin_edges[i + 1]).toFixed(2)}`
    );
  } else if (type === 'categorical' && Array.isArray(histogram.counts) && Array.isArray(histogram.categories)) {
    histValues = histogram.counts;
    histLabels = histogram.categories;
  }

  const rows = [];
  if (type === 'numerical') {
    rows.push(['Mean', fmtNum(stats.mean)], ['Median', fmtNum(stats.median)],
      ['Min', fmtNum(stats.min)], ['Max', fmtNum(stats.max)]);
  } else if (type === 'categorical') {
    rows.push(['Top', String(stats.most_common ?? '–')], ['Unique', fmtNum(stats.unique_count)]);
  } else if (type === 'date') {
    if (stats.min_date != null) rows.push(['From', String(stats.min_date)]);
    if (stats.max_date != null) rows.push(['To', String(stats.max_date)]);
  }
  rows.push(['Count', fmtNum(stats.count)]);
  if (stats.null_count > 0) rows.push(['Missing', fmtNum(stats.null_count)]);

  return (
    <Card style={{ flexShrink: 0, width: 248, boxShadow: 'none' }}>
      <CardContent style={{ display: 'flex', flexDirection: 'column', gap: 14, padding: 18 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
          <span className="q-truncate" style={{ fontSize: 15, fontWeight: 500, color: 'var(--foreground)' }} title={feature.name}>
            {feature.name}
          </span>
          <span><Badge variant="outline"><TypeIcon aria-hidden />{typeMeta.label}</Badge></span>
        </div>

        {histValues.length > 0 ? (
          <MiniHistogram values={histValues} labels={histLabels} />
        ) : (
          <div className="q-muted" style={{ height: 64, display: 'grid', placeItems: 'center', fontSize: 14 }}>
            No distribution
          </div>
        )}

        <dl className="q-dl">
          {rows.map(([label, value]) => (
            <div key={label} className="q-dl__row">
              <dt>{label}</dt>
              <dd className="q-truncate" title={value}>{value}</dd>
            </div>
          ))}
        </dl>

        {warning && (
          <Badge variant="warning" style={{ whiteSpace: 'normal', borderRadius: 8, alignItems: 'flex-start', padding: '6px 8px' }}>
            <TriangleAlert aria-hidden style={{ marginTop: 1 }} />
            <span>{warning.hint || warning.issue}</span>
          </Badge>
        )}
      </CardContent>
    </Card>
  );
};

// -----------------------------------------------------------------------------
// Correlation pair: both columns, a strength bar and the coefficient.
// -----------------------------------------------------------------------------

const strength = (abs) => {
  if (abs >= 0.7) return { label: 'Strong', variant: 'success' };
  if (abs >= 0.4) return { label: 'Moderate', variant: 'warning' };
  return { label: 'Weak', variant: 'secondary' };
};

const CorrelationCard = ({ pair }) => {
  const v = Number(pair?.value || 0);
  const abs = Math.min(Math.abs(v), 1);
  const s = strength(abs);
  const radius = 32;
  const stroke = 6;
  const c = 2 * Math.PI * radius;
  return (
    <div className="q-corr">
      <div className="q-corr__ring">
        <svg width="76" height="76" viewBox="0 0 76 76" aria-hidden>
          <circle cx="38" cy="38" r={radius} fill="none" stroke="color-mix(in srgb, var(--foreground) 8%, transparent)" strokeWidth={stroke} />
          <circle
            cx="38" cy="38" r={radius} fill="none"
            stroke="var(--chart-1)" strokeWidth={stroke} strokeLinecap="round"
            strokeDasharray={`${c * abs} ${c}`}
            transform="rotate(-90 38 38)"
          />
        </svg>
        <span className="q-corr__value">{abs.toFixed(2)}</span>
      </div>
      <span className="q-corr__col q-truncate" title={pair.col1}>{pair.col1}</span>
      <span className="q-corr__vs">vs</span>
      <span className="q-corr__col q-truncate" title={pair.col2}>{pair.col2}</span>
      <Badge variant={s.variant}><span aria-hidden className="ui-badge__dot" />{s.label}</Badge>
    </div>
  );
};

// -----------------------------------------------------------------------------
// Block.
// -----------------------------------------------------------------------------

const DataAnalysis = ({ blockData = {}, targetColumn = '' }) => {
  const features = Array.isArray(blockData.features) ? blockData.features : [];
  // Memoized because the filter/option hooks below depend on it — a fresh []
  // on every render would make them recompute every time.
  const correlations = useMemo(
    () => (Array.isArray(blockData.correlations) ? blockData.correlations : []),
    [blockData.correlations],
  );
  const warnings = Array.isArray(blockData.warnings) ? blockData.warnings : [];
  const totalRows = blockData.total_rows;

  const warningByColumn = useMemo(() => {
    const m = {};
    for (const w of warnings) {
      if (w && w.column && !m[w.column]) m[w.column] = w;
    }
    return m;
  }, [warnings]);

  const [showAllCorrelations, setShowAllCorrelations] = useState(false);
  // '' = no filter. Holds a column name; only pairs containing it are shown.
  const [filterColumn, setFilterColumn] = useState('');

  // Options come from the columns that actually appear in a pair, not from
  // `features` — every entry is then guaranteed to match at least one card.
  // The target leads (it is what people filter by first); the rest are
  // alphabetical, case-insensitively so casing doesn't scatter related names.
  const filterOptions = useMemo(() => {
    // The block's own target wins: it is what the analysis actually ran
    // against. `targetColumn` is the analysis's current target, used only
    // for blocks persisted before the payload carried one.
    const target = blockData.target_column || targetColumn || '';
    const cols = new Set();
    for (const p of correlations) {
      if (p && p.col1) cols.add(p.col1);
      if (p && p.col2) cols.add(p.col2);
    }
    const hasTarget = cols.delete(target);
    const rest = [...cols].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
    const others = rest.map(c => ({ value: c, label: c }));
    return [
      { value: '', label: 'All columns', separatorAfter: !hasTarget },
      // The badge says Target, so the label stays the bare column name.
      ...(hasTarget ? [{ value: target, label: target, badge: 'Target', separatorAfter: true }] : []),
      ...others,
    ];
  }, [correlations, blockData.target_column, targetColumn]);

  const filteredCorrs = useMemo(() => (
    filterColumn
      ? correlations.filter(p => p.col1 === filterColumn || p.col2 === filterColumn)
      : correlations
  ), [correlations, filterColumn]);

  const visibleCorrs = showAllCorrelations ? filteredCorrs : filteredCorrs.slice(0, 5);

  const description = [
    `${features.length} features`,
    totalRows != null && `${Number(totalRows).toLocaleString()} rows`,
    warnings.length > 0 && `${warnings.length} quality warning${warnings.length === 1 ? '' : 's'}`,
  ].filter(Boolean).join(' · ');

  return (
    <Block
      icon="bar-chart-3"
      title="Data overview"
      description={description}
      badge={<Badge variant="success"><CircleCheck aria-hidden />Ready</Badge>}
    >
      <div className="q-section">
        <div className="q-section__head" style={{ minHeight: 0 }}>
          <span className="q-section__title">Features</span>
          <Badge variant="secondary" className="ui-badge--mono">{features.length}</Badge>
        </div>
        {features.length === 0 ? (
          <div className="q-muted" style={{ fontSize: 15 }}>No features computed.</div>
        ) : (
          <div className="q-hscroll">
            {features.map((f) => (
              <FeatureCard key={f.name} feature={f} warning={warningByColumn[f.name]} />
            ))}
          </div>
        )}
      </div>

      {correlations.length > 0 && (
        <div className="q-section">
          <div className="q-section__head" style={{ flexWrap: 'wrap' }}>
            <span className="q-section__title">Top correlations</span>
            <div className="q-section__actions">
              {filterOptions.length > 2 && (
                <Select
                  size="sm"
                  align="end"
                  value={filterColumn}
                  onValueChange={setFilterColumn}
                  options={filterOptions}
                  label="Show pairs involving"
                  ariaLabel="Filter correlations by column"
                  className="q-corr-filter"
                  minMenuWidth={220}
                />
              )}
              {filteredCorrs.length > 5 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowAllCorrelations(v => !v)}
                >
                  {showAllCorrelations ? 'Show top 5' : `View all (${filteredCorrs.length})`}
                </Button>
              )}
            </div>
          </div>
          {visibleCorrs.length === 0 ? (
            <div className="q-muted" style={{ fontSize: 15 }}>
              No correlations involving {filterColumn}.
            </div>
          ) : (
            <div className="q-hscroll">
              {visibleCorrs.map((p, i) => (
                <CorrelationCard key={`${p.col1}|${p.col2}|${i}`} pair={p} />
              ))}
            </div>
          )}
        </div>
      )}
    </Block>
  );
};

export default DataAnalysis;
